/**
 * Utilidades compartidas por el tipo de gráfico "Texto narrativo": arma una oración fija con
 * marcadores {{clave}} reemplazados por valores calculados a partir de filas agrupadas
 * (la misma forma de datos que ya produce Barras: una columna de agrupación + una de valor).
 */

/** Si la plantilla tiene al menos un marcador {{clave}}: si no tiene ninguno, es texto fijo
 * puro, no depende de ningún dato de la consulta. */
export function hasNarrativePlaceholder(template) {
  return /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(template || '');
}

/**
 * Columnas que son o terminan en un identificador interno (id, managerId, customer_id,
 * sensor_id, assetId, etc.) no son datos de negocio: exponerlas como marcador de texto
 * narrativo solo ensucia la lista de "Marcadores disponibles" con IDs crudos sin valor de
 * lectura.
 */
export function isInternalIdColumn(column) {
  const name = String(column || '').trim();
  if (!name) return false;
  if (/^id$/i.test(name)) return true;
  // Sufijo snake_case "..._id" (customer_id, sensor_id, zone_id, parent_id): convención
  // real de las tablas vsaas_* de este proyecto.
  if (/_id$/i.test(name)) return true;
  // Sufijo camelCase "...Id" (managerId, customerId, projectId, sensorId, assetId): letra
  // minúscula o dígito justo antes de "Id". No alcanza con terminar en "id" sin más
  // (/id$/i), porque eso también atrapa columnas de negocio reales que solo comparten esas
  // dos letras al final sin ningún separador de palabra (grid, paid, hybrid, valid).
  return /[a-z0-9]Id$/.test(name);
}

/** Convierte un valor de fila (ej. "no_seguridad", "Casos de Éxito") en una clave de marcador. */
export function slugifyPlaceholderKey(raw) {
  const normalized = String(raw ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  const slug = normalized
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'valor';
}

/**
 * Evita que dos columnas de filtro distintas que generan el mismo slug (ej. "Categoría" y
 * "categoria" slugifican ambas a "categoria") se pisen en silencio en el mapa de marcadores
 * (ver renderNarrativeSegments: last-write-wins por key). La segunda coincidencia en adelante
 * agrega un sufijo numérico, mismo patrón que ya usa sanitizeSheetName en excelExport.js para
 * nombres de pestaña repetidos.
 */
export function dedupePlaceholderKey(key, usedKeys) {
  if (!usedKeys.has(key)) {
    usedKeys.add(key);
    return key;
  }
  let suffix = 2;
  let candidate = `${key}_${suffix}`;
  while (usedKeys.has(candidate)) {
    suffix += 1;
    candidate = `${key}_${suffix}`;
  }
  usedKeys.add(candidate);
  return candidate;
}

/**
 * A partir de filas [{ [xAlias]: etiqueta, [yAlias]: numero }], arma la lista de marcadores
 * disponibles: {{total}}, {{<clave>}}, {{<clave>_pct}} por cada fila, y {{top_label}}/
 * {{top_value}}/{{top_pct}} para la fila con mayor valor.
 */
export function buildNarrativePlaceholders(rows, xAlias, yAlias) {
  if (!xAlias || !yAlias || !Array.isArray(rows) || !rows.length) return [];
  const entries = rows
    .map((row) => ({
      label: String(row?.[xAlias] ?? ''),
      key: slugifyPlaceholderKey(row?.[xAlias]),
      value: Number(row?.[yAlias]) || 0,
    }))
    .filter((e) => e.label !== '');
  if (!entries.length) return [];

  const total = entries.reduce((sum, e) => sum + e.value, 0);
  const pct = (value) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0);

  const list = [{ key: 'total', label: 'Total', valueText: String(total) }];
  entries.forEach((e) => {
    list.push({ key: e.key, label: e.label, valueText: String(e.value) });
    list.push({ key: `${e.key}_pct`, label: `${e.label} (%)`, valueText: String(pct(e.value)) });
  });

  const top = entries.reduce((best, e) => (!best || e.value > best.value ? e : best), null);
  if (top) {
    list.push({ key: 'top_label', label: 'Categoría con mayor valor', valueText: top.label });
    list.push({ key: 'top_value', label: 'Valor de esa categoría', valueText: String(top.value) });
    list.push({ key: 'top_pct', label: 'Porcentaje de esa categoría', valueText: String(pct(top.value)) });
  }
  return list;
}

/** Reemplaza cada {{clave}} del template por su valor; deja el marcador tal cual si no hay dato. */
export function renderNarrativeTemplate(template, placeholders) {
  if (!template) return '';
  const map = Object.fromEntries((placeholders || []).map((p) => [p.key, p.valueText]));
  return String(template).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) =>
    map[key] != null ? map[key] : match
  );
}

/**
 * Igual que renderNarrativeTemplate, pero en vez de un string arma una lista de tramos
 * { type: 'text' | 'value', value } para que el dato calculado se pueda resaltar distinto del
 * texto fijo de la plantilla (ej. negrita + color) sin perder cuáles partes son cuáles.
 */
export function renderNarrativeSegments(template, placeholders) {
  if (!template) return [];
  const map = Object.fromEntries((placeholders || []).map((p) => [p.key, p.valueText]));
  const segments = [];
  const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let lastIndex = 0;
  let match;
  const str = String(template);
  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: str.slice(lastIndex, match.index) });
    }
    const [full, key] = match;
    const resolved = map[key];
    segments.push({ type: resolved != null ? 'value' : 'text', value: resolved != null ? resolved : full });
    lastIndex = match.index + full.length;
  }
  if (lastIndex < str.length) {
    segments.push({ type: 'text', value: str.slice(lastIndex) });
  }
  return segments;
}
